import React, { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { useUniqueId } from 'src/hooks'
import { Format, If, Button, HeightFade } from 'src/Components'
import {
  Heading,
  ProConList,
  Pro,
  Info,
  Strong
} from 'src/Components/shared/Gem/GemDetail'
import CTAHeading from 'src/Components/homeowners/PurchasingPower/CTAHeading'
import FeeRange from 'src/Components/homeowners/PurchasingPower/FeeRange'
import AdditionalInfo from 'src/Components/shared/AdditionalInfo'
import CMASection from 'src/Components/shared/EstimatedValue/CMASection'
import { selectInstantOfferEnabled } from 'src/store/selectors/powerUps'
import { selectSellPocketAmount } from 'src/store/selectors/home/equity/sellPocket'
import { redrawHeightFadeKey } from 'src/actions/transitions'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import history from 'src/history'

import styles from './SellPocketDetail.module.scss'

const SellPocket = () => {
  const dispatch = useDispatch()
  const heightFadeId = useUniqueId('DreamHome_HF_')
  const refCMA = useRef(null)
  const refIBuy = useRef(null)
  const [showCMA, setShowCMA] = useState(false)
  const [cmaStep, setCmaStep] = useState(null)
  const iBuyEnabled = useSelector(selectInstantOfferEnabled)
  const cashout = useSelector(selectSellPocketAmount)

  return (
    <div>
      <Heading>
        <FormattedMessage
          id='Homeowner.purchasePower.sellPocketHeading'
          defaultMessage='Are you ready to cash in on your equity? You could sell your home and pocket around {cashout}.'
          values={{
            cashout: (
              <Strong>
                <Format.Currency abbreviate value={cashout} />
              </Strong>
            )
          }}
        />
      </Heading>

      <ProConList className={styles.proCon}>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.sellPocketOne'
            defaultMessage='You may be exempt from capital gains taxes if you’ve owned and lived in the home for at least two years out of the last five'
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.sellPocketTwo'
            defaultMessage='Up to $250,000 in profit may be tax-free for unmarried individuals and up to $500,000 for married couples'
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.sellPocketThree'
            defaultMessage='You could also downsize into a cheaper home using a portion of your equity and save the rest'
          />
        </Pro>
        <Info>
          <FeeRange />
        </Info>
        <Info>
          <FormattedMessage
            id='Homeowner.purchasePower.sellPocketFour'
            defaultMessage='Always consult a professional tax advisor to understand implications for your specific situation'
          />
        </Info>
      </ProConList>

      <CTAHeading className={styles.cta}>
        <FormattedMessage
          id='Homeowner.purchasePower.ctaHeading'
          defaultMessage="So, what's next?"
        />
      </CTAHeading>

      <Button.Group className={styles.buttonWrapper}>
        <Button
          ref={refCMA}
          onClick={() => {
            history.push(OVERLAY_HELPERS.tuneYourValue.buildPath())
          }}
        >
          <FormattedMessage
            id='Homeowner.purchasePower.sellPocketButton.cma'
            defaultMessage='Verify listing price'
          />
        </Button>
        <If condition={iBuyEnabled}>
          <Button
            ref={refIBuy}
            onClick={() => {
              setShowCMA(false)
              history.push(OVERLAY_HELPERS.instantOffer.buildPath())
            }}
          >
            <FormattedMessage
              id='Homeowner.instantOffer.cta'
              defaultMessage='Instant Offer'
            />
          </Button>
        </If>
      </Button.Group>

      <AdditionalInfo
        visible={showCMA}
        cardWidth
        showDisclaimers={false}
        targetRef={showCMA ? refCMA.current : refIBuy.current}
        className={classnames(styles.bottomFlush, showCMA && styles.bottomOpen)}
      >
        <HeightFade
          id={heightFadeId}
          speed={cmaStep && showCMA ? 'fast' : 'medium'}
        >
          <If condition={showCMA}>
            <CMASection
              onStepChange={step => {
                setCmaStep(step)
                dispatch(redrawHeightFadeKey(heightFadeId, 'fast'))
              }}
              close={() => {
                setCmaStep(null)
                setShowCMA(false)
                dispatch(redrawHeightFadeKey(heightFadeId))
              }}
            />
          </If>
          <If condition={!showCMA}>
            <span />
          </If>
        </HeightFade>
      </AdditionalInfo>
    </div>
  )
}

export default SellPocket
